import { DecimalPipe } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { VulcanIconComponent } from '@eagle-i/vulcan-component-library';
import { Store } from '@ngrx/store';
import {
  Observable,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  of,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs';

import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import { County } from '../../../../../../../core/classes/county';
import { Territory } from '../../../../../../../core/classes/territory';
import { ReportsService } from '../../../../../../../services/reports.service';
import { Coverage } from '../../../../../../../shared/classes/coverage';
import { selectCounties, selectTerritories } from '../../../../../../../state/location/location.reducer';
import { BaseStateReportWidget } from '../base-state-report-widget';

interface CountyCoverage {
  name: string;
  percentage: number;
}

@Component({
  selector: 'tgrid-coverage-table-widget',
  templateUrl: './coverage-table-widget.component.html',
  styleUrls: ['./coverage-table-widget.component.scss'],
  imports: [MatCardModule, MatButtonModule, VulcanIconComponent, DecimalPipe],
})
export class CoverageTableWidgetComponent extends BaseStateReportWidget {

  private reportsService = inject(ReportsService);
  private store = inject(Store);

  protected stateName = signal<string>('');
  protected year = signal<number>(0);
  protected countiesWithLeastCoverage = signal<CountyCoverage[]>([]);
  protected countiesWithNoData = signal<CountyCoverage[]>([]);

  constructor() {
    super();
    this.loadData();
  }


  protected override async export(): Promise<void> {
    const filename = `county_coverage_${this.year()}_${this.stateName()}`;
    const zip = new JSZip();

    const textContent = this.exportContent()?.nativeElement.innerText;
    const textBlob = new Blob([textContent], { type: 'text/plain' });
    zip.file(`${filename}.txt`, textBlob);

    try {
      const canvas = await html2canvas(this.exportContent()?.nativeElement);
      const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve));
      if (blob) {
        zip.file(`${filename}.png`, blob);
        const content = await zip.generateAsync({ type: 'blob' });
        const url = URL.createObjectURL(content);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filename}.zip`;
        link.click();
        URL.revokeObjectURL(url);
      }
      this.snackBar.open('Export downloaded successfully', 'Close', { duration: 3000 });
    } catch (err) {
      console.error(err);
      this.snackBar.open('Export failed. Please try again.', 'Close', { duration: 3000 });
    }
  }


  private loadData(): void {
    const routeParams = <T>(param: string): Observable<T> =>
      this.activatedRoute.params.pipe(
        map(params => params[param]),
        distinctUntilChanged(),
        filter(x => !!x),
        takeUntilDestroyed(),
        shareReplay(1),
      ) as Observable<T>;

    const year$ = routeParams<number>('year');
    const state$ = routeParams<string>('stateAbbr');
    year$.pipe(takeUntilDestroyed()).subscribe(year => this.year.set(year));

    const territory$ = state$.pipe(
      switchMap(abbv => this.store.select(selectTerritories).pipe(
        map(territories => territories.find(t => t.abbreviation === abbv)),
      )),
      tap(territory => territory && this.stateName.set(territory.name)),
      shareReplay(1),
    );

    const countyData$ = combineLatest([year$, territory$]).pipe(
      switchMap(([year, territory]) => territory ? this.getData(year, territory) : of([])),
      startWith([]),
      shareReplay(1),
    );

    const counties$ = state$.pipe(
      switchMap(abbv => this.store.select(selectCounties).pipe(
        map(counties => counties.filter(c => c.stateAbbreviation === abbv)),
      )),
    );

    combineLatest([countyData$, counties$]).pipe(
      map(([coverageData, counties]) => this.processCoverageData(coverageData, counties)),
      takeUntilDestroyed(),
    ).subscribe(({ countiesWithLeastCoverage, countiesWithNoData }) => {
      this.countiesWithLeastCoverage.set(countiesWithLeastCoverage);
      this.countiesWithNoData.set(countiesWithNoData);
    });
  }

  private processCoverageData(coverageData: Coverage[], counties: County[]): {
    countiesWithLeastCoverage: CountyCoverage[],
    countiesWithNoData: CountyCoverage[];
  } {
    const coverageMap = new Map(coverageData.map(c => [c.fipsCode, c]));
    const countiesWithLeastCoverage: CountyCoverage[] = [];
    const countiesWithNoData: CountyCoverage[] = [];

    counties.forEach(county => {
      const coverage = coverageMap.get(county.geoid);
      const percentage = coverage?.percentage ?? 0;

      if (percentage > 0 && percentage < 50) {
        countiesWithLeastCoverage.push({ name: county.name, percentage });
      } else if (percentage === 0) {
        countiesWithNoData.push({ name: county.name, percentage: 0 });
      }
    });

    return {
      countiesWithLeastCoverage: countiesWithLeastCoverage.sort((a, b) => a.name.localeCompare(b.name)),
      countiesWithNoData: countiesWithNoData.sort((a, b) => a.name.localeCompare(b.name)),
    };
  }

  private getData(year: number, territory: Territory): Observable<Coverage[]> {
    return this.reportsService.getCountyCoverageData(year, [territory]).pipe(
      catchError(error => {
        console.error('Error fetching coverage data:', error);
        return of([]);
      }),
    );
  }
}
